import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";

export interface ValidateEVSEParams {
  evseID: string;
}

const validateEVSE = async (params: ValidateEVSEParams, signal?: AbortSignal): Promise<boolean> => {
  const response = await axios.get(`/api/evse/${params.evseID}/validate/`, { signal });
  return response.data as boolean;
};

type QueryKeyType = readonly ["evse", "validate", ValidateEVSEParams | "disabled"];

export const validateEVSEKeys = {
  all: ["evse"] as const,
  validates: () => [...validateEVSEKeys.all, "validate"] as const,
  validate: (params: ValidateEVSEParams | "disabled") => [...validateEVSEKeys.validates(), params] as const
};

export const useValidateEVSE = (
  params: ValidateEVSEParams | null,
  options?: Omit<UseQueryOptions<boolean, Error, boolean, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID;

  return useQuery({
    queryKey: params ? validateEVSEKeys.validate(params) : validateEVSEKeys.validate("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("EVSE validation params are required");
      }
      return validateEVSE(params, signal);
    },
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isEnabled,
    ...options
  });
};
